<script lang="ts">
  import { onMount, onDestroy } from "svelte"
  import type { Chart as ChartInstance } from "chart.js"

  let { data = { labels: [], values: [] }, kind = "bar" }: { data: { labels: string[]; values: number[] }; kind: "bar" | "line" } = $props()

  let canvas: HTMLCanvasElement | undefined = $state()
  let chart: ChartInstance | null = null

  type ChartTheme = {
    accent: string
    border: string
    textSecondary: string
  }

  const fallbackTheme: ChartTheme = {
    accent: "#3E6AE1",
    border: "#E3E5E8",
    textSecondary: "#5F6368"
  }

  const themeColor = (name: string, fallback: string) => {
    const css = getComputedStyle(document.documentElement)
    return css.getPropertyValue(name).trim() || fallback
  }

  const resolveChartTheme = (): ChartTheme => ({
    accent: themeColor("--accent", fallbackTheme.accent),
    border: themeColor("--border", fallbackTheme.border),
    textSecondary: themeColor("--text-secondary", fallbackTheme.textSecondary)
  })

  let chartTheme = $state<ChartTheme>(fallbackTheme)
  let themeObserver: MutationObserver | null = null

  // 디자인 시스템 준수: 최댓값 또는 피크(Peak) 시점만 브랜드 컬러로 강조하고, 나머지는 차분한 보조선 색으로 채색
  const maxVal = $derived(data.values.length > 0 ? Math.max(...data.values) : 0)
  const resolveBarColors = (theme: ChartTheme) =>
    data.values.map(val => (val === maxVal && val > 0) ? theme.accent : theme.border)
  const barColors = $derived(resolveBarColors(chartTheme))

  const applyChartTheme = () => {
    const nextTheme = resolveChartTheme()
    chartTheme = nextTheme

    if (!chart) return

    const dataset = chart.data.datasets[0]
    if (!dataset) return

    dataset.backgroundColor = kind === "bar" ? resolveBarColors(nextTheme) : nextTheme.accent
    dataset.borderColor = nextTheme.accent

    const xTicks = chart.options.scales?.x?.ticks
    if (xTicks) xTicks.color = nextTheme.textSecondary

    const yTicks = chart.options.scales?.y?.ticks
    if (yTicks) yTicks.color = nextTheme.textSecondary

    const yGrid = chart.options.scales?.y?.grid
    if (yGrid) yGrid.color = nextTheme.border

    chart.update()
  }

  const totalTokens = $derived(data.values.reduce((sum, val) => sum + val, 0))
  const averageTokens = $derived(data.values.length > 0 ? Math.round(totalTokens / data.values.length) : 0)

  onMount(async () => {
    if (!canvas) return
    chartTheme = resolveChartTheme()

    const { Chart, registerables } = await import("chart.js")
    Chart.register(...registerables)

    chart = new Chart(canvas, {
      type: kind,
      data: {
        labels: data.labels,
        datasets: [{
          data: data.values,
          backgroundColor: kind === "bar" ? barColors : chartTheme.accent,
          borderColor: chartTheme.accent,
          borderWidth: kind === "line" ? 2 : 0,
          borderRadius: 4,
          maxBarThickness: 32
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: {
        ticks: { color: chartTheme.textSecondary, font: { size: 11 } },
            grid: { display: false }
          },
          y: {
        ticks: { color: chartTheme.textSecondary, font: { size: 11 } },
        grid: { color: chartTheme.border }
          }
        }
      }
    })

    themeObserver = new MutationObserver(applyChartTheme)
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"]
    })
  })

  onDestroy(() => {
    themeObserver?.disconnect()
    chart?.destroy()
    themeObserver = null
    chart = null
  })
</script>

<div class="relative" style="height: 200px;">
  {#if data.labels.length > 0}
    <canvas bind:this={canvas} aria-label="사용량 차트"></canvas>
  {:else}
    <div class="flex h-full items-center justify-center gate-panel gate-muted">
      아직 표시할 사용량이 없습니다.
    </div>
  {/if}
</div>

{#if data.labels.length > 0}
  <!-- 디자인 시스템 준수: 차트 하단에 접근성을 고려한 구조적 텍스트 요약 및 Tabular Numbers 제공 -->
  <div class="mt-4 pt-3 border-t border-[var(--border)]" role="region" aria-label="사용량 데이터 요약">
    <dl class="grid grid-cols-3 gap-4 text-center">
      <div class="flex flex-col items-center">
        <dt class="text-[11px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">총 사용량</dt>
        <dd class="text-base font-bold text-[var(--accent)] mt-1 tabular-nums" style="font-variant-numeric: tabular-nums;">
          {totalTokens.toLocaleString()} <span class="text-xs font-normal text-[var(--text-secondary)]">Tokens</span>
        </dd>
      </div>
      <div class="flex flex-col items-center border-x border-[var(--border)] px-2">
        <dt class="text-[11px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">일평균</dt>
        <dd class="text-base font-bold text-[var(--accent)] mt-1 tabular-nums" style="font-variant-numeric: tabular-nums;">
          {averageTokens.toLocaleString()} <span class="text-xs font-normal text-[var(--text-secondary)]">Tokens</span>
        </dd>
      </div>
      <div class="flex flex-col items-center">
        <dt class="text-[11px] font-bold text-[var(--accent)] uppercase tracking-wider">최고 피크</dt>
        <dd class="text-base font-bold text-[var(--accent)] mt-1 tabular-nums" style="font-variant-numeric: tabular-nums;">
          {maxVal.toLocaleString()} <span class="text-xs font-normal text-[var(--accent)]">Tokens</span>
        </dd>
      </div>
    </dl>
  </div>
{/if}
